import InnerContainer from "@/components/ContentContainer/InnerContainer";
import FixedInput from "@/components/FixedInput";
import Skeleton from "@/components/Skeleton/Skeleton";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import {
  CheckOutlined,
  DeleteOutlined,
  HourglassOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Button, Flex, Input, Tooltip } from "antd";
import TextArea from "antd/es/input/TextArea";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DeleteMaterialAction from "../DeleteMaterialAction";
import { Material } from "../MaterialOverview/MaterialsOverview";
import { useGetMaterial, usePatchMaterial } from "../useMaterials";
import styles from "./MaterialDetail.module.scss";

export default function MaterialDetails({ materialId }: { materialId: number }) {
  const { data: material } = useGetMaterial({ materialId });
  const patch = usePatchMaterial();
  const smartQueryClient = useSmartQueryClient();
  const navigate = useNavigate();

  const saveName = useCallback((name: string) => {
    patch.mutateAsync({ materialId, patch: { name } });
  }, []);

  const debouncedSaveName = useMemo(() => _.debounce(saveName, 250), [saveName]);

  const updateName = (name: string) => {
    smartQueryClient.setQueryData<Material>(["material", materialId, "details"], (material) => {
      return material
        ? {
            ...material,
            name,
          }
        : undefined;
    });

    debouncedSaveName(name);
  };

  return (
    <InnerContainer
      title="Details"
      className={styles.MaterialDetails}
      contentClassName={styles.content}
    >
      <div className={styles.row}>
        Name
        <FixedInput
          value={material?.name}
          onChange={(name) => updateName(name)}
        />
      </div>
      <div className={styles.row}>
        Supplier
        <Input
          value={material?.supplier_name || ""}
          disabled={true}
        />
      </div>
      <div className={styles.row}>
        Supplier address
        <Flex
          align="center"
          gap={8}
        >
          <TextArea
            style={{ flexGrow: 1, height: 100 }}
            value={material?.supplier_address}
            disabled={true}
          />
          {material?.supplier_address_is_valid === false ? (
            <Tooltip title="Address could not be validated - matching suggestions will be less accurate as geography can not be taken into account">
              <InfoCircleOutlined style={{ color: "#FA8C16" }} />
            </Tooltip>
          ) : (
            material?.supplier_address_is_valid === null && (
              <Tooltip title="Validating address">
                <HourglassOutlined />
              </Tooltip>
            )
          )}
        </Flex>
      </div>
      <div className={styles.buttons}>
        {material ? (
          <DeleteMaterialAction
            materials={[material]}
            onSucces={() => navigate("/matching/materials")}
          >
            <Button
              style={{ textAlign: "left" }}
              disabled={material.relations > 0}
              danger
            >
              <DeleteOutlined /> Delete
            </Button>
          </DeleteMaterialAction>
        ) : (
          <Skeleton style={{ width: "24px", height: "24px" }} />
        )}
        {patch.isLoading ? (
          <span>
            <LoadingOutlined /> Saving...
          </span>
        ) : (
          <span>
            <CheckOutlined /> Saved
          </span>
        )}
      </div>
    </InnerContainer>
  );
}
